import React, { FC, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import DatePicker from 'components/date-picker/date-picker';
import TimePicker from 'components/time-picker/time-picker';
import { parseLocalDateTime, formatDateDisplay, toIsoLocal, makeLocalMidnight } from '../utils/date';

const useStyles = makeStyles((theme: any) => ({
  fieldContainer: {
    marginBottom: 0,
  },
  label: {
    fontSize: '0.875rem',
    fontWeight: 500,
    marginBottom: theme.spacing(0.75),
    display: 'block',
  },
  dateTimeRow: {
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      gap: theme.spacing(1),
      alignItems: 'stretch',
    },
  },
  datePickerWrapper: {
    flex: 1,
    minWidth: 200,
    '& .MuiFilledInput-root': {
      minHeight: 40,
      height: 40,
    },
    '& .MuiFilledInput-input': {
      lineHeight: '1.5',
    },
  },
  timePickerWrapper: {
    flex: 1,
    minWidth: 200,
    '& .MuiFilledInput-root': {
      minHeight: 40,
      height: 40,
    },
    '& .MuiFilledInput-input': {
      lineHeight: '1.5',
    },
  },
  error: {
    color: theme.palette.error.main,
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.5),
  },
}));

export interface DateTimePickerFieldProps {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
  error?: string;
  required?: boolean;
}

export const DateTimePickerField: FC<DateTimePickerFieldProps> = ({
  label,
  value,
  onChange,
  disabled = false,
  error,
  required = false,
}) => {
  const classes = useStyles();

  const parsed = useMemo(() => parseLocalDateTime(value || ''), [value]);

  const dateDisplay = useMemo(() => {
    if (parsed.valid && parsed.parts) {
      return formatDateDisplay(parsed.parts.month, parsed.parts.day, parsed.parts.year);
    }
    return null;
  }, [parsed]);

  /**
   * Create a Date object for the TimePicker.
   * If we have explicit time, use year/month/day/hours/minutes.
   * If date-only, use year/month/day at midnight (00:00:00).
   * If no value, return null (picker will show current time, but onChange won't emit without complete data).
   */
  const timePickerValue = useMemo(() => {
    if (parsed.valid && parsed.parts) {
      const date = makeLocalMidnight(
        parsed.parts.year,
        parsed.parts.month,
        parsed.parts.day,
      );
      if (parsed.hasExplicitTime && parsed.parts.hours >= 0) {
        date.setHours(parsed.parts.hours, parsed.parts.minutes, 0, 0);
      }
      return date;
    }
    return null;
  }, [parsed]);

  const handleDateChange = useCallback(
    (newDateIso: string | null | undefined) => {
      if (!newDateIso) {
        onChange(null);
        return;
      }

      const dateOnly = newDateIso.split('T')[0];
      if (!dateOnly) {
        onChange(null);
        return;
      }

      const [yearStr, monthStr, dayStr] = dateOnly.split('-');
      const year = parseInt(yearStr, 10);
      const month = parseInt(monthStr, 10);
      const day = parseInt(dayStr, 10);

      if (!year || !month || !day) {
        onChange(null);
        return;
      }

      // Check current time picker value to see if we have explicit time
      if (parsed.valid && parsed.parts && parsed.hasExplicitTime && parsed.parts.hours >= 0) {
        const h = parsed.parts.hours;
        const m = parsed.parts.minutes;
        const newValue = toIsoLocal(year, month, day, h, m, 0);
        onChange(newValue);
      } else {
        // Date-only: use current local time at selection moment
        const now = new Date();
        const h = now.getHours();
        const m = now.getMinutes();
        const newValue = toIsoLocal(year, month, day, h, m, 0);
        onChange(newValue);
      }
    },
    [parsed, onChange]
  );

  /**
   * Handle TimePicker onChange: extract hours/minutes from the Date,
   * combine with current date, and emit ISO string only if we have
   * a valid date and explicit time.
   */
  const handleTimeChange = useCallback(
    (date: Date | null) => {
      if (!date || !parsed.valid || !parsed.parts) {
        return;
      }

      const hours = date.getHours();
      const minutes = date.getMinutes();

      // Only emit if both are valid (>= 0 and within valid ranges)
      if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
        const newValue = toIsoLocal(
          parsed.parts.year,
          parsed.parts.month,
          parsed.parts.day,
          hours,
          minutes,
          0
        );
        onChange(newValue);
      }
    },
    [parsed, onChange]
  );

  return (
    <Box className={classes.fieldContainer}>
      <Typography className={classes.label}>
        {label}
        {required && ' *'}
      </Typography>

      <Box className={classes.dateTimeRow}>
        <Box className={classes.datePickerWrapper}>
          <DatePicker
            value={dateDisplay || null}
            onChange={handleDateChange}
            disabled={disabled}
            placeholder="MM/DD/YYYY"
            disableToolbar={false}
          />
        </Box>

        <Box className={classes.timePickerWrapper}>
          <TimePicker
            value={timePickerValue}
            onChange={handleTimeChange}
            disabled={disabled}
          />
        </Box>
      </Box>

      {error && <Typography className={classes.error}>{error}</Typography>}
    </Box>
  );
};

export default DateTimePickerField;
